import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  NativeSelect,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import {
  useDeleteSubscription,
  useSubscriptions,
  type RecipientRule,
  type SubscriptionDto,
} from '../api/notifications';
import { useOrgs } from '../api/orgs';
import { SubscriptionModal } from '../components/subscription-modal';

const CONSUMER_STREAMS = [
  'case.created',
  'case.workflow.completed',
  'case.images_check.completed',
  'consent.check.completed',
  'ai_review.completed',
  'ai_review.started',
  'ai_review.failed',
  'ai_review.superseded',
  'human_review.completed',
  'human_review.failed',
  'workflow.started',
  'workflow.halted',
  'workflow.cancelled',
] as const;

function recipientSummary(rule: RecipientRule): string {
  switch (rule.type) {
    case 'slackChannel':
      return `#${rule.channel}`;
    case 'emailLiteral':
      return rule.address;
    case 'webhook':
      return rule.url;
    case 'user':
      return `user:${rule.path}`;
    case 'email':
      return `email:${rule.path}`;
  }
}

export function NotificationsSubscriptionsPage() {
  const { data, isLoading, isError } = useSubscriptions();
  const { data: orgsData } = useOrgs();
  const orgOptions = (orgsData ?? []).map((org) => ({ value: org.id, label: org.name }));
  const deleteMutation = useDeleteSubscription();

  const [filterEvent, setFilterEvent] = useState('All');
  const [filterEnabled, setFilterEnabled] = useState('All');
  const [editing, setEditing] = useState<SubscriptionDto | undefined>(undefined);
  const [deleting, setDeleting] = useState<SubscriptionDto | null>(null);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  function openCreateModal() {
    setEditing(undefined);
    openModal();
  }

  function openEditModal(sub: SubscriptionDto) {
    setEditing(sub);
    openModal();
  }

  function handleDelete() {
    if (!deleting) return;
    deleteMutation.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
  }

  const filtered = (data ?? []).filter((sub) => {
    if (filterEvent !== 'All' && sub.eventType !== filterEvent) return false;
    if (filterEnabled === 'Enabled' && !sub.enabled) return false;
    if (filterEnabled === 'Disabled' && sub.enabled) return false;
    return true;
  });

  if (isLoading) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  if (isError) {
    return <Alert color="red">Failed to load subscriptions</Alert>;
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Subscriptions</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
          Add subscription
        </Button>
      </Group>

      <Group gap="sm">
        <NativeSelect
          label="Event type"
          data={['All', ...CONSUMER_STREAMS]}
          value={filterEvent}
          onChange={(e) => setFilterEvent(e.currentTarget.value)}
        />
        <NativeSelect
          label="Enabled"
          data={['All', 'Enabled', 'Disabled']}
          value={filterEnabled}
          onChange={(e) => setFilterEnabled(e.currentTarget.value)}
        />
      </Group>

      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Event type</Table.Th>
            <Table.Th>Template</Table.Th>
            <Table.Th>Recipient</Table.Th>
            <Table.Th>Enabled</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filtered.map((sub) => (
            <Table.Tr key={sub.id}>
              <Table.Td>{sub.eventType}</Table.Td>
              <Table.Td>{sub.templateCode}</Table.Td>
              <Table.Td>{recipientSummary(sub.recipientRule)}</Table.Td>
              <Table.Td>
                <Badge color={sub.enabled ? 'green' : 'gray'}>
                  {sub.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <ActionIcon variant="subtle" onClick={() => openEditModal(sub)}>
                    <IconPencil size={16} />
                  </ActionIcon>
                  <ActionIcon variant="subtle" color="red" onClick={() => setDeleting(sub)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <SubscriptionModal
        opened={modalOpened}
        onClose={closeModal}
        initial={editing}
        orgOptions={orgOptions}
      />

      <Modal opened={!!deleting} onClose={() => setDeleting(null)} title="Delete subscription">
        <Text>
          Delete subscription for <strong>{deleting?.eventType}</strong>?
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button color="red" loading={deleteMutation.isPending} onClick={handleDelete}>
            Delete
          </Button>
        </Group>
      </Modal>
    </Stack>
  );
}
